import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaCheck, FaTimes, FaFilm, FaTv } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const CategoryManagerPage = () => {
  const [activeTab, setActiveTab] = useState('movie'); // 'movie' | 'series'
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const handleError = useCallback((err) => {
    if (err.response && err.response.status === 401) {
      setError('Sessão expirada. Por favor, faça login novamente.');
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      navigate('/login');
    } else {
      setError(err.response?.data?.message || err.message || 'Ocorreu um erro');
    }
  }, [navigate]);

  const fetchCategories = useCallback(async (tab) => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    setSelectedIds([]);
    try {
      const endpoint = tab === 'movie' ? '/api/movies/categories/existing' : '/api/series/categories/existing';
      const response = await axios.get(`${BACKEND_URL}${endpoint}`);
      setCategories(response.data || []);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories(activeTab);
    }
  }, [isAuthenticated, activeTab, fetchCategories]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const selectAll = () => setSelectedIds(categories.map((c) => c.id));
  const deselectAll = () => setSelectedIds([]);

  const itemLabel = activeTab === 'movie' ? 'filme(s)' : 'série(s)';

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;

    const selectedCategories = categories.filter((c) => selectedIds.includes(c.id));
    const totalItems = selectedCategories.reduce((sum, c) => sum + c.itemCount, 0);
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedCategories.length} categoria(s)?\n` +
      `Isso vai apagar também ${totalItems} ${itemLabel} dentro delas.\n\n` +
      `Essa ação não pode ser desfeita.`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setError('');
    setSuccessMessage('');
    try {
      const endpoint = activeTab === 'movie' ? '/api/movies/categories' : '/api/series/categories';
      const response = await axios.delete(`${BACKEND_URL}${endpoint}`, {
        data: { categoryIds: selectedIds }
      });
      const deletedItems = activeTab === 'movie' ? response.data.deletedMovies : response.data.deletedSeries;
      setSuccessMessage(
        `${response.data.deletedCategories} categoria(s) excluída(s) com sucesso ` +
        `(${deletedItems} ${itemLabel} removido(s)).`
      );
      await fetchCategories(activeTab);
    } catch (err) {
      handleError(err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return <p>Por favor, faça login para acessar esta página.</p>;
  }

  const selectedCount = selectedIds.length;
  const selectedItemCount = categories
    .filter((c) => selectedIds.includes(c.id))
    .reduce((sum, c) => sum + c.itemCount, 0);

  return (
    <div className="movie-page">
      <h1 className="movie-page__title">Gerenciar Categorias</h1>
      <p>Marque categorias já existentes no XUI para excluir, junto com todo o conteúdo que estiver dentro delas.</p>

      <div className="movie-page__card">
        <div className="movie-page__category-controls">
          <button
            onClick={() => setActiveTab('movie')}
            className={`movie-page__btn ${activeTab === 'movie' ? 'movie-page__btn--primary' : 'movie-page__btn--secondary'}`}
          >
            <FaFilm /> Filmes
          </button>
          <button
            onClick={() => setActiveTab('series')}
            className={`movie-page__btn ${activeTab === 'series' ? 'movie-page__btn--primary' : 'movie-page__btn--secondary'}`}
          >
            <FaTv /> Séries
          </button>
        </div>
      </div>

      {error && <p className="movie-page__error">{error}</p>}
      {successMessage && <p className="movie-page__success-message">{successMessage}</p>}

      <div className="movie-page__card">
        <h2 className="movie-page__card-title">Categorias existentes</h2>

        {isLoading ? (
          <p>Carregando...</p>
        ) : categories.length === 0 ? (
          <p>Nenhuma categoria encontrada.</p>
        ) : (
          <>
            <div className="movie-page__category-controls">
              <button onClick={selectAll} className="movie-page__btn movie-page__btn--secondary">
                <FaCheck /> Selecionar Todas
              </button>
              <button onClick={deselectAll} className="movie-page__btn movie-page__btn--secondary">
                <FaTimes /> Desmarcar Todas
              </button>
            </div>

            <div className="movie-page__categories-list">
              {categories.map((category) => (
                <div key={category.id} className="movie-page__category-item">
                  <input
                    type="checkbox"
                    id={`existing-category-${category.id}`}
                    checked={selectedIds.includes(category.id)}
                    onChange={() => toggleSelect(category.id)}
                  />
                  <label htmlFor={`existing-category-${category.id}`}>{category.category_name}</label>
                  <span> — {category.itemCount} {activeTab === 'movie' ? 'filmes' : 'séries'}</span>
                </div>
              ))}
            </div>

            <div className="movie-page__stats-grid">
              <div className="movie-page__stats-card">
                <h3>Categorias selecionadas</h3>
                <p>{selectedCount}</p>
              </div>
              <div className="movie-page__stats-card">
                <h3>Itens que serão apagados</h3>
                <p>{selectedItemCount}</p>
              </div>
            </div>

            <button
              onClick={handleDelete}
              disabled={isDeleting || selectedCount === 0}
              className="movie-page__btn movie-page__btn--danger"
            >
              <FaTrash /> {isDeleting ? 'Excluindo...' : 'Excluir Selecionadas'}
            </button>
          </>
        )}
      </div>

      <footer className="movie-page__footer">
        <p>© 2024 Sync Filmes. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default CategoryManagerPage;
